/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 sidebar: greeting with next-up activity, Jump to (filters the menu in
 * place; the header search is the global one), favourites, collapsible
 * sections, plan card, and a 68px icon rail. The items are exactly the classic Sidenav's (see sidebarModel.js);
 * this component only changes how they look.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  Calendar,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  CloudRain,
  Moon,
  Search,
  Snowflake,
  Star,
  Sun,
  Sunrise,
  Sunset,
  Wind,
  X,
} from "lucide-react";
import { useAuth } from "auth/Auth";
import { usePermissionsContext } from "auth/PermissionsProvider";
import { getNotificationRedirectPath } from "utils/notificationRedirect";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import { NavIcon } from "./navIcons";
import { activeItemKey, buildSidebarSections, filterSections, flattenItems, splitHome } from "./sidebarModel";
import { applyApp, useActiveApp } from "./apps";
import useShellPrefs from "./useShellPrefs";
import { useWeather, partOfDay, displayName } from "./greeting";

export const SIDEBAR_WIDTH = 248;
export const RAIL_WIDTH = 68;

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: t.text3,
};

const TIME_ICONS = { Morning: Sunrise, Afternoon: Sun, Evening: Sunset, Night: Moon };
const WEATHER_ICONS = { rain: CloudRain, snow: Snowflake, wind: Wind };

export function Greeting({ collapsed }) {
  const { user } = useAuth();
  const name = displayName(user);
  const part = partOfDay(new Date());
  const { weather, enabled: weatherOn, enable } = useWeather();
  const BadgeIcon = (weather?.kind && WEATHER_ICONS[weather.kind]) || TIME_ICONS[part];
  const initial = (name || "?").charAt(0);

  const avatar = (
    <Box sx={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
      {/* The uploaded profile picture (Auth resolves it into user.avatar);
          the initial shows without one or if the image fails to load. */}
      <Avatar
        src={user?.avatar || undefined}
        alt={name || ""}
        sx={{
          width: 40,
          height: 40,
          bgcolor: v2Tones.purple.bg,
          color: v2Tones.purple.fg,
          border: "1.5px solid #9B7FE0",
          fontWeight: 700,
          fontSize: 16,
          fontFamily: "inherit",
        }}
      >
        {initial}
      </Avatar>
      <Box
        sx={{
          position: "absolute",
          right: -7,
          bottom: -6,
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: t.surface,
          border: `1px solid ${t.border}`,
          display: "grid",
          placeItems: "center",
          color: "#B45309",
        }}
      >
        <BadgeIcon size={13} aria-hidden="true" />
      </Box>
    </Box>
  );

  if (collapsed) {
    return (
      <Tooltip title={`${part}, ${name}`} placement="right">
        <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>{avatar}</Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", px: 1.75, pt: 2, pb: 1.25 }}>
      {avatar}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 17, lineHeight: "22px", color: t.text, fontWeight: 500 }} noWrap>
          {part}, <Box component="span" sx={{ fontWeight: 800 }}>{name}</Box>
        </Typography>
        {weatherOn && weather ? (
          <Typography sx={{ fontSize: 12, color: t.text2 }} noWrap>
            {Math.round(weather.temperature)}°C · {weather.label}
          </Typography>
        ) : (
          <ButtonBase
            onClick={enable}
            sx={{ fontSize: 12, color: t.link, fontFamily: "inherit", "&:hover": { textDecoration: "underline" } }}
          >
            Show local weather
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
}
Greeting.propTypes = { collapsed: PropTypes.bool.isRequired };

/** Next scheduled activity for this user over the coming week. */
function useNextUp() {
  const { axiosInstance, user } = useAuth();
  const [next, setNext] = useState(null);

  useEffect(() => {
    if (!axiosInstance || !user?.id) return undefined;
    let cancelled = false;
    const today = new Date();
    const end = new Date(today.getTime() + 7 * 86400000);
    const iso = (d) => d.toISOString().slice(0, 10);
    axiosInstance
      .get("/activities/", {
        params: { user_id: user.id, filter_status: "Scheduled", due_date_start: iso(today), due_date_end: iso(end) },
      })
      .then(({ data }) => {
        if (cancelled || !Array.isArray(data)) return;
        const now = Date.now();
        const when = (a) => {
          const base = a.due_date ? new Date(a.due_date) : null;
          if (!base) return null;
          if (a.starttime && !a.all_day) {
            const [h, m] = String(a.starttime).split(":");
            base.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
          }
          return base;
        };
        const upcoming = data
          .map((a) => ({ a, at: when(a) }))
          .filter((x) => x.at && x.at.getTime() >= now - 15 * 60000)
          .sort((x, y) => x.at - y.at);
        setNext(upcoming[0] || null);
      })
      .catch(() => setNext(null));
    return () => {
      cancelled = true;
    };
  }, [axiosInstance, user?.id]);

  return next;
}

function NextUp({ collapsed }) {
  const next = useNextUp();
  const navigate = useNavigate();
  if (!next) return null;
  const { a, at } = next;
  const path = getNotificationRedirectPath(a.parent_table, a.parent_table_id);
  const time = a.all_day
    ? "All day"
    : at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = at.toDateString() === new Date().toDateString() ? "" : `${at.toLocaleDateString([], { weekday: "short" })} `;
  const label = a.title || a.type || "Activity";
  const open = () => path && navigate(path);

  if (collapsed) {
    return (
      <Tooltip title={`Next up · ${day}${time} · ${label}`} placement="right">
        <IconButton onClick={open} sx={{ mx: "auto", display: "flex", width: 44, height: 40 }} aria-label="Next up">
          <Calendar size={18} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <ButtonBase
      onClick={open}
      disabled={!path}
      sx={{
        mx: 1.5,
        mb: 1,
        p: 1.25,
        width: "calc(100% - 24px)",
        display: "flex",
        gap: 1.25,
        alignItems: "center",
        justifyContent: "flex-start",
        textAlign: "left",
        border: `1px solid ${t.border}`,
        borderRadius: "10px",
        bgcolor: t.surface,
        fontFamily: "inherit",
        "&:hover": { bgcolor: t.hover },
      }}
    >
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: v2Tones.info.bg, color: v2Tones.info.fg, display: "grid", placeItems: "center", flexShrink: 0 }}>
        <Calendar size={16} aria-hidden="true" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ ...sectionLabelSx, fontSize: 11 }}>Next up · {day}{time}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.text }} noWrap>{label}</Typography>
        {a.type && <Typography sx={{ fontSize: 12, color: t.text2 }} noWrap>{a.type}</Typography>}
      </Box>
    </ButtonBase>
  );
}
NextUp.propTypes = { collapsed: PropTypes.bool.isRequired };

function NavItem({ item, active, collapsed, depth, favourite, onToggleFavourite }) {
  const content = (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        height: collapsed ? 40 : 36,
        px: collapsed ? 0 : 1.25,
        pl: collapsed ? 0 : 1.75 + depth * 1.5,
        justifyContent: collapsed ? "center" : "flex-start",
        mx: collapsed ? 1 : 1.25,
        borderRadius: "8px",
        color: active ? t.primaryDark : t.text,
        bgcolor: active ? "rgba(21, 101, 200, 0.09)" : "transparent",
        fontWeight: active ? 600 : 400,
        fontSize: 13.33,
        "&:hover": { bgcolor: active ? "rgba(21, 101, 200, 0.12)" : t.hover },
        "&:hover .v2-fav": { opacity: 1 },
        "&::before": active
          ? { content: '""', position: "absolute", left: collapsed ? -8 : 0, top: 9, bottom: 9, width: 3, borderRadius: 2, bgcolor: t.primary }
          : {},
      }}
    >
      <Box sx={{ display: "grid", placeItems: "center", width: 20, color: active ? t.primary : t.text2, "& .material-icons-round, & .MuiIcon-root": { fontSize: "18px !important" } }}>
        <NavIcon icon={item.icon} />
      </Box>
      {!collapsed && (
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.name}
        </Box>
      )}
      {!collapsed && onToggleFavourite && (
        <IconButton
          className="v2-fav"
          size="small"
          aria-label={favourite ? `Remove ${item.name} from favourites` : `Add ${item.name} to favourites`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFavourite(item.key);
          }}
          sx={{ opacity: favourite ? 1 : 0, p: 0.5, color: favourite ? "#B45309" : t.text3, "&:focus-visible": { opacity: 1 } }}
        >
          <Star size={14} fill={favourite ? "#E0A100" : "none"} />
        </IconButton>
      )}
    </Box>
  );

  const link = item.href ? (
    <a href={item.href} target="_blank" rel="noreferrer" style={{ textDecoration: "none", display: "block" }}>
      {content}
    </a>
  ) : (
    <NavLink to={item.route} style={{ textDecoration: "none", display: "block" }} aria-current={active ? "page" : undefined}>
      {content}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip title={item.name} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
}
NavItem.propTypes = {
  item: PropTypes.object.isRequired,
  active: PropTypes.bool,
  collapsed: PropTypes.bool,
  depth: PropTypes.number,
  favourite: PropTypes.bool,
  onToggleFavourite: PropTypes.func,
};
NavItem.defaultProps = { active: false, collapsed: false, depth: 0, favourite: false, onToggleFavourite: null };

function GroupItem({ item, activeKey, collapsed, favourites, onToggleFavourite, forceOpen }) {
  const childActive = item.children.some((c) => c.key === activeKey);
  const [userOpen, setOpen] = useState(true);
  const open = forceOpen || userOpen;
  if (collapsed) {
    return item.children.map((c) => (
      <NavItem key={c.key} item={c} active={c.key === activeKey} collapsed />
    ));
  }
  return (
    <Box>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          width: "calc(100% - 20px)",
          mx: 1.25,
          height: 36,
          px: 1.25,
          pl: 1.75,
          gap: 1.25,
          borderRadius: "8px",
          justifyContent: "flex-start",
          fontFamily: "inherit",
          fontSize: 13.33,
          color: childActive ? t.primaryDark : t.text,
          fontWeight: childActive ? 600 : 400,
          "&:hover": { bgcolor: t.hover },
        }}
      >
        <Box sx={{ display: "grid", placeItems: "center", width: 20, color: t.text2 }}>
          <NavIcon icon={item.icon} />
        </Box>
        <Box component="span" sx={{ flex: 1, textAlign: "left" }}>{item.name}</Box>
        <ChevronDown size={16} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform 150ms" }} color={t.text3} />
      </ButtonBase>
      <Collapse in={open} timeout="auto">
        {item.children.map((c) => (
          <NavItem
            key={c.key}
            item={c}
            depth={1}
            active={c.key === activeKey}
            favourite={favourites.includes(c.key)}
            onToggleFavourite={onToggleFavourite}
          />
        ))}
      </Collapse>
    </Box>
  );
}
GroupItem.propTypes = {
  item: PropTypes.object.isRequired,
  activeKey: PropTypes.string,
  collapsed: PropTypes.bool.isRequired,
  favourites: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleFavourite: PropTypes.func.isRequired,
  forceOpen: PropTypes.bool,
};
GroupItem.defaultProps = { activeKey: null, forceOpen: false };

function PlanCard() {
  const { subscription } = usePermissionsContext();
  if (!subscription?.plan_name) return null;
  const status = (subscription.subscription_status || "").toLowerCase();
  const days = subscription.days_remaining;
  const tone = subscription.in_grace || (typeof days === "number" && days <= 7) ? v2Tones.warning : v2Tones.neutral;
  return (
    <Box sx={{ mx: 1.5, mb: 1.5, p: 1.5, border: `1px solid ${t.border}`, borderRadius: "10px", bgcolor: t.surface }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.text }} noWrap>
          {subscription.plan_name}
        </Typography>
        {status && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 600, px: 0.75, py: 0.25, borderRadius: "6px", bgcolor: tone.bg, color: tone.fg, textTransform: "capitalize" }}>
            {status}
          </Box>
        )}
      </Box>
      {typeof days === "number" && (
        <Typography sx={{ fontSize: 12, color: t.text2, mt: 0.5 }}>
          {days >= 0 ? `${days} day${days === 1 ? "" : "s"} left in this term` : "Term ended"}
        </Typography>
      )}
    </Box>
  );
}

export default function SidebarV2({ sidenavRoutes, mobile, onNavigate }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const prefs = useShellPrefs();
  const collapsed = !mobile && prefs.collapsed;

  const { app, ownedKeys } = useActiveApp();
  const { home, sections } = useMemo(
    () => splitHome(applyApp(buildSidebarSections(sidenavRoutes), app, ownedKeys)),
    [sidenavRoutes, app, ownedKeys]
  );
  const allItems = useMemo(
    () => (home ? [home, ...flattenItems(sections)] : flattenItems(sections)),
    [home, sections]
  );
  const activeKey = activeItemKey(allItems, pathname);

  // Jump to: narrows the menu as you type. While a query is in, favourites
  // step aside (they would repeat matches) and every section and group shows
  // open, so a match is never hidden inside a collapsed one.
  const [query, setQuery] = useState("");
  const filterRef = useRef(null);
  const filtering = query.trim().length > 0;
  const shownSections = useMemo(() => filterSections(sections, query), [sections, query]);
  const shownHome = home && (!filtering || filterSections([{ key: "home", title: null, items: [home] }], query).length > 0) ? home : null;
  const firstMatch = filtering ? [...(shownHome ? [shownHome] : []), ...flattenItems(shownSections)][0] : null;
  const noMatches = filtering && !shownHome && shownSections.length === 0;

  const onFilterKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setQuery("");
    } else if (e.key === "Enter" && firstMatch) {
      e.preventDefault();
      if (firstMatch.href) window.open(firstMatch.href, "_blank", "noreferrer");
      else navigate(firstMatch.route);
      setQuery("");
    }
  };

  // From the rail, the search icon opens the full sidebar with the filter
  // focused, since there is no room to type in 68px.
  const openFilterFromRail = () => {
    prefs.setCollapsed(false);
    setTimeout(() => filterRef.current?.focus(), 160);
  };
  const favouriteItems = prefs.favourites
    .map((key) => allItems.find((i) => i.key === key))
    .filter(Boolean);

  // Close the mobile drawer after a navigation -- not on mount, or the drawer
  // would close itself the moment it opens.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    setQuery("");
    if (mobile && onNavigate) onNavigate();
  }, [pathname, mobile, onNavigate]);

  return (
    <Box
      component="nav"
      aria-label="Main"
      sx={{
        width: collapsed ? RAIL_WIDTH : mobile ? 300 : SIDEBAR_WIDTH,
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: t.panel,
        borderRight: `1px solid ${t.border}`,
        transition: "width 150ms ease",
        overflow: "hidden",
      }}
    >
      <Greeting collapsed={collapsed} />
      <NextUp collapsed={collapsed} />

      {collapsed ? (
        <Tooltip title="Jump to…" placement="right">
          <IconButton onClick={openFilterFromRail} aria-label="Jump to" sx={{ mx: "auto", display: "flex", width: 44, height: 40, mb: 1 }}>
            <Search size={18} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box
          sx={{
            mx: 1.5,
            mb: 1.5,
            height: 32,
            px: 1.25,
            gap: 1,
            display: "flex",
            alignItems: "center",
            border: `1px solid ${t.border}`,
            borderRadius: "8px",
            bgcolor: t.surface,
            color: t.text3,
            "&:focus-within": { borderColor: t.primary, boxShadow: `0 0 0 3px rgba(21, 101, 200, 0.15)` },
          }}
        >
          <Search size={15} aria-hidden="true" />
          <Box
            component="input"
            ref={filterRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onFilterKeyDown}
            placeholder="Jump to…"
            aria-label="Jump to a menu item"
            aria-controls="v2-sidebar-menu"
            autoComplete="off"
            spellCheck={false}
            sx={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              border: 0,
              outline: 0,
              p: 0,
              bgcolor: "transparent",
              color: t.text,
              fontFamily: "inherit",
              fontSize: 13,
              "&::placeholder": { color: t.text3, opacity: 1 },
              "&::-webkit-search-cancel-button": { display: "none" },
            }}
          />
          {filtering && (
            <IconButton size="small" aria-label="Clear" onClick={() => { setQuery(""); filterRef.current?.focus(); }} sx={{ p: 0.25, color: t.text3 }}>
              <X size={14} />
            </IconButton>
          )}
        </Box>
      )}

      <Box id="v2-sidebar-menu" sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 1 }}>
        {noMatches && (
          <Typography role="status" sx={{ px: 2, py: 1.5, fontSize: 13, color: t.text2 }}>
            No menu items match “{query.trim()}”.
          </Typography>
        )}
        {!filtering && favouriteItems.length > 0 && (
          <Box sx={{ mb: 1 }}>
            {!collapsed && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 0.75 }}>
                <Star size={12} fill="#E0A100" color="#E0A100" aria-hidden="true" />
                <Typography sx={sectionLabelSx}>Favourites</Typography>
              </Box>
            )}
            {favouriteItems.map((item) => (
              <NavItem
                key={`fav-${item.key}`}
                item={item}
                active={item.key === activeKey}
                collapsed={collapsed}
                favourite
                onToggleFavourite={prefs.toggleFavourite}
              />
            ))}
          </Box>
        )}

        {/* Home: the pinned dashboard, the same in every app. */}
        {shownHome && (
          <Box sx={{ mb: 0.5 }}>
            <NavItem
              item={shownHome}
              active={shownHome.key === activeKey}
              collapsed={collapsed}
              favourite={prefs.favourites.includes(shownHome.key)}
              onToggleFavourite={prefs.toggleFavourite}
            />
          </Box>
        )}

        {shownSections.map((section) => {
          const sectionClosed = !filtering && prefs.collapsedSections.includes(section.key);
          return (
            <Box key={section.key} sx={{ mb: 0.5 }}>
              {section.title &&
                (collapsed ? (
                  <Box sx={{ height: 1, bgcolor: t.border, mx: 1.5, my: 1 }} />
                ) : (
                  <ButtonBase
                    onClick={filtering ? undefined : () => prefs.toggleSection(section.key)}
                    aria-expanded={!sectionClosed}
                    sx={{ width: "100%", px: 2, py: 0.75, justifyContent: "space-between", fontFamily: "inherit" }}
                  >
                    <Typography sx={sectionLabelSx}>{section.title}</Typography>
                    <ChevronDown
                      size={14}
                      color={t.text3}
                      style={{ transform: sectionClosed ? "rotate(-90deg)" : "none", transition: "transform 150ms" }}
                    />
                  </ButtonBase>
                ))}
              <Collapse in={collapsed || !sectionClosed} timeout="auto">
                {section.items.map((item) =>
                  item.children.length && !item.route ? (
                    <GroupItem
                      key={item.key}
                      item={item}
                      activeKey={activeKey}
                      collapsed={collapsed}
                      favourites={prefs.favourites}
                      onToggleFavourite={prefs.toggleFavourite}
                      forceOpen={filtering}
                    />
                  ) : (
                    <NavItem
                      key={item.key}
                      item={item}
                      active={item.key === activeKey}
                      collapsed={collapsed}
                      favourite={prefs.favourites.includes(item.key)}
                      onToggleFavourite={prefs.toggleFavourite}
                    />
                  )
                )}
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {!collapsed && <PlanCard />}

      {!mobile && (
        <ButtonBase
          onClick={() => prefs.setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          sx={{
            height: 44,
            borderTop: `1px solid ${t.border}`,
            gap: 1,
            px: 2,
            justifyContent: collapsed ? "center" : "flex-start",
            color: t.text2,
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          {!collapsed && "Collapse menu"}
        </ButtonBase>
      )}
    </Box>
  );
}

SidebarV2.propTypes = {
  sidenavRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
  mobile: PropTypes.bool,
  onNavigate: PropTypes.func,
};
SidebarV2.defaultProps = { mobile: false, onNavigate: null };
